import React, { useCallback, useEffect, useState } from 'react';
import { Download, FileText, PackagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { field, ghost, primary, rs, Stat, safe, useApi } from '../purchasing/ui';

const REPORTS = [['po-by-vendor', 'POs by vendor'], ['returns', 'Purchase returns'], ['stock-movement', 'Opening vs closing stock'], ['out-of-stock', 'Out of stock']];
const ago = (d) => new Date(Date.now() - d * 864e5).toISOString().slice(0, 10);

const Trend = ({ points }) => {
  const max = Math.max(1, ...points.map((p) => p.value));
  return (
    <div className="flex h-28 items-end gap-1 rounded-xl border bg-white p-3" data-testid="stock-trend">
      {points.map((p) => <div key={p.date} title={`${p.date}: ${rs(p.value)}`} className="flex-1 rounded-t bg-teal-500/80" style={{ height: `${(p.value / max) * 100}%` }} />)}
    </div>
  );
};

export default function PurchasingReports() {
  const { call, download } = useApi();
  const [key, setKey] = useState('po-by-vendor');
  const [frm, setFrm] = useState(ago(30));
  const [to, setTo] = useState(ago(0));
  const [vendor, setVendor] = useState('');
  const [vendors, setVendors] = useState([]);
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [sel, setSel] = useState([]);
  const qs = `frm=${frm}&to=${to}&vendor_id=${vendor}`;
  const load = useCallback(() => call('get', `/purchasing/reports/${key}?${qs}`).then((r) => setD({ ...r, _key: key })), [call, key, qs]);
  useEffect(() => { call('get', '/purchasing/vendors').then(setVendors); }, [call]);
  useEffect(() => { setD(null); setSel([]); load(); }, [load]);
  const reorder = safe(async () => {
    const r = await call('post', '/purchasing/pos/reorder-low-stock', sel);
    toast.success(`${r.created} draft PO(s) created`);
    if (r.skipped?.length) toast.warning(`${r.skipped.length} part(s) skipped, no preferred vendor set`);
    setSel([]); load();
  });
  return (
    <div className="min-h-full space-y-4 bg-slate-50 p-4 sm:p-6" data-testid="purchasing-reports">
      <h1 className="text-2xl font-bold">Purchasing & Stock reports</h1>
      <div className="flex gap-1 overflow-x-auto border-b">{REPORTS.map(([k, l]) => <button key={k} onClick={() => setKey(k)} className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-semibold ${key === k ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500'}`} data-testid={`report-tab-${k}`}>{l}</button>)}</div>
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" value={frm} onChange={(e) => setFrm(e.target.value)} className={`${field.replace('w-full ', '')} w-40`} data-testid="report-from" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${field.replace('w-full ', '')} w-40`} data-testid="report-to" />
        <select value={vendor} onChange={(e) => setVendor(e.target.value)} className={`${field.replace('w-full ', '')} w-52`} data-testid="report-vendor"><option value="">All vendors</option>{vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
        <button className={ghost} onClick={() => download(`/purchasing/reports/${key}?${qs}&fmt=xlsx`, `${key}.xlsx`)} data-testid="report-xlsx"><Download size={14} /> Excel</button>
        <button className={ghost} onClick={() => download(`/purchasing/reports/${key}?${qs}&fmt=pdf`, `${key}.pdf`)} data-testid="report-pdf"><FileText size={14} /> PDF</button>
        {key === 'out-of-stock' && <button className={`${primary} ml-auto`} disabled={!sel.length} onClick={reorder} data-testid="report-create-po"><PackagePlus size={14} /> Create PO ({sel.length})</button>}
      </div>
      {d && d._key === key && <>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{Object.entries(d.totals).map(([k, v]) => <Stat key={k} label={k} value={k.includes('₹') ? rs(v) : v} testid={`report-total-${k}`} />)}</div>
        {key === 'stock-movement' && d.trend?.length > 1 && <Trend points={d.trend} />}
        {key === 'returns' && <div className="flex flex-wrap gap-2 text-xs">{Object.entries(d.by_reason || {}).map(([k, v]) => <span key={k} className="rounded-full bg-white px-3 py-1 border">{k}: {rs(v)}</span>)}</div>}
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full text-sm" data-testid="report-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>{key === 'out-of-stock' && <th className="p-2" />}{d.headers.map((h) => <th key={h} className="p-2">{h}</th>)}</tr></thead>
            <tbody>{d.table.map((r, i) => (
              <React.Fragment key={i}>
                <tr className="border-t hover:bg-slate-50" onClick={() => key === 'po-by-vendor' && setOpen(open === i ? null : i)} data-testid={`report-row-${i}`}>
                  {key === 'out-of-stock' && <td className="p-2"><input type="checkbox" checked={sel.includes(d.rows[i].item_id)} onChange={(e) => setSel(e.target.checked ? [...sel, d.rows[i].item_id] : sel.filter((x) => x !== d.rows[i].item_id))} data-testid={`report-select-${i}`} /></td>}
                  {r.map((c, j) => <td key={j} className="p-2">{c ?? '—'}</td>)}
                </tr>
                {key === 'po-by-vendor' && open === i && d.rows[i].pos.map((p) => (
                  <tr key={p.number} className="bg-slate-50 text-xs"><td className="p-2 pl-6 font-mono">{p.number}</td><td className="p-2">{p.date}</td><td className="p-2">{p.status}</td><td className="p-2">{p.items} items</td><td className="p-2">{rs(p.value)}</td><td className="p-2">{p.received_pct}% received</td><td /></tr>))}
              </React.Fragment>))}</tbody>
          </table>
          {!d.table.length && <p className="p-8 text-center text-sm text-slate-500">Nothing in this period.</p>}
        </div>
      </>}
    </div>
  );
}
